import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Loader2 } from "lucide-react";
import api from "../lib/api";

function LegalLayout({ title, children }) {
  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <Link to="/" data-testid="legal-back-link" className="inline-flex items-center gap-2 text-[#6B6B6B] hover:text-[#2F4F4F] mb-8 transition-colors text-sm">
          <ArrowLeft className="w-4 h-4" /> Retour au site
        </Link>
        <div className="mb-3"><span className="section-num font-mono-tech text-xs tracking-widest text-[#C46A4A]">§ LEGAL // DOCUMENT</span></div>
        <h1 className="text-4xl font-medium tracking-tight mb-8 text-[#2F4F4F] font-montserrat">{title}</h1>
        <div className="text-[#555555] leading-relaxed whitespace-pre-wrap text-sm">{children}</div>
      </div>
    </div>
  );
}

export function CGVPage() {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/legal/cgv").then(({ data }) => setContent(data.content)).catch(() => setContent("Erreur lors du chargement des CGV.")).finally(() => setLoading(false));
  }, []);

  return (
    <LegalLayout title="Conditions Générales de Vente">
      <Helmet>
        <title>CGV — MonBeauSite | Conditions Générales de Vente</title>
        <meta name="description" content="Conditions Générales de Vente de MonBeauSite, créateur de sites internet professionnels pour commerçants, artisans et restaurateurs en France." />
      </Helmet>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#2F4F4F]" /></div> : content}
    </LegalLayout>
  );
}

export function MentionsLegalesPage() {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/legal/mentions-legales").then(({ data }) => setContent(data.content)).catch(() => setContent("Erreur lors du chargement des mentions légales.")).finally(() => setLoading(false));
  }, []);

  return (
    <LegalLayout title="Mentions Légales">
      <Helmet>
        <title>Mentions Légales — MonBeauSite</title>
        <meta name="description" content="Mentions légales de MonBeauSite, créateur de sites internet professionnels en France." />
      </Helmet>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#2F4F4F]" /></div> : content}
    </LegalLayout>
  );
}

export function PolitiqueCookiesPage() {
  return (
    <LegalLayout title="Politique de Cookies">
      <Helmet>
        <title>Politique de Cookies — MonBeauSite</title>
        <meta name="description" content="Politique de cookies de MonBeauSite. Informations sur les cookies utilisés sur notre site." />
      </Helmet>
      {`POLITIQUE DE COOKIES — MonBeauSite

Dernière mise à jour : Avril 2026

1. QU'EST-CE QU'UN COOKIE ?
Un cookie est un petit fichier texte déposé sur votre terminal (ordinateur, tablette, smartphone) lors de votre visite sur notre site. Il permet de stocker des informations relatives à votre navigation.

2. LES COOKIES QUE NOUS UTILISONS

2.1 Cookies essentiels (obligatoires)
Ces cookies sont indispensables au fonctionnement du site. Ils comprennent :
- Cookie d'authentification (access_token) : permet de maintenir votre session connectée.
- Cookie de consentement (mbs_cookie_consent) : enregistre vos préférences en matière de cookies.
Durée : Session / 1 an.
Base légale : Intérêt légitime (fonctionnement technique du site).

2.2 Cookies analytiques (optionnels)
Ces cookies nous aident à comprendre comment les visiteurs interagissent avec le site, en collectant des informations de manière anonyme.
Durée : 13 mois maximum.
Base légale : Consentement.

2.3 Cookies marketing (optionnels)
Ces cookies permettent de vous proposer du contenu et des publicités adaptés à vos centres d'intérêt.
Durée : 13 mois maximum.
Base légale : Consentement.

3. GESTION DE VOS PRÉFÉRENCES
Lors de votre première visite, un bandeau vous permet de :
- Tout accepter : active tous les cookies.
- Refuser les optionnels : seuls les cookies essentiels restent actifs.
- Personnaliser : choisir individuellement les catégories de cookies.

Vous pouvez modifier vos préférences à tout moment en supprimant vos cookies dans les paramètres de votre navigateur.

4. COMMENT SUPPRIMER LES COOKIES ?
Chaque navigateur offre la possibilité de gérer les cookies :
- Chrome : Paramètres > Confidentialité et sécurité > Cookies
- Firefox : Options > Vie privée et sécurité > Cookies
- Safari : Préférences > Confidentialité > Cookies
- Edge : Paramètres > Confidentialité > Cookies

5. DURÉE DE CONSERVATION
Votre choix de consentement est conservé pendant 12 mois. À l'issue de cette période, le bandeau de consentement réapparaîtra.

6. CONTACT
Pour toute question relative à notre politique de cookies :
MonBeauSite — Emilien TERROT
Email : bonjour@monbeausite.com
26200 Montélimar, France

---
TVA non applicable, article 293 B du CGI.`}
    </LegalLayout>
  );
}


export function PolitiqueConfidentialitePage() {
  return (
    <LegalLayout title="Politique de Confidentialité">
      <Helmet>
        <title>Politique de Confidentialité — MonBeauSite</title>
        <meta name="description" content="Politique de confidentialité de MonBeauSite. Informations sur la collecte et le traitement de vos données personnelles conformément au RGPD." />
      </Helmet>
      {`POLITIQUE DE CONFIDENTIALITE — MonBeauSite
Derniere mise a jour : avril 2026

MonBeauSite (Emilien TERROT, entrepreneur individuel) s'engage a proteger la vie privee des utilisateurs de son site internet monbeausite.com. Cette politique de confidentialite explique quelles donnees nous collectons, pourquoi et comment nous les utilisons.

---

1. RESPONSABLE DU TRAITEMENT
Emilien TERROT — MonBeauSite
20 quai du Roubion, 26200 Montelimar, France
Email : bonjour@monbeausite.com
SIRET : 51126097800040

2. DONNEES COLLECTEES

2.1 Via le formulaire de contact
- Nom de l'entreprise
- Nom du responsable
- Adresse email
- Numero de telephone (facultatif)
- Description du projet (facultatif)
- Consentement au recontact
- Consentement aux communications marketing (facultatif)

2.2 Via l'espace client
- Nom, prenom, denomination sociale
- Adresse email de connexion
- SIRET
- Telephone
- Adresse de facturation
- Signature electronique (contrat)
- Fichiers joints aux demandes de modification

2.3 Via le paiement
Les paiements sont traites par Stripe (stripe.com). MonBeauSite ne stocke aucune donnee bancaire. Stripe est certifie PCI-DSS.

2.4 Cookies
Voir notre Politique de Cookies pour le detail des cookies utilises.

3. FINALITES ET BASES LEGALES

| Finalite | Base legale | Duree de conservation |
|---|---|---|
| Recontacter un prospect | Consentement | 3 ans apres le dernier contact |
| Gestion du contrat client | Execution du contrat | Duree du contrat + 5 ans |
| Envoi de communications marketing | Consentement | Jusqu'au retrait du consentement |
| Facturation et comptabilite | Obligation legale | 10 ans |
| Amelioration du service | Interet legitime | 13 mois (donnees anonymisees) |

4. PARTAGE DES DONNEES
Vos donnees ne sont jamais vendues ni louees a des tiers.
Elles peuvent etre partagees uniquement avec nos sous-traitants techniques :
- Stripe (paiements) — Etats-Unis — Clauses contractuelles types
- Resend (envoi d'emails) — Etats-Unis — Clauses contractuelles types
- Emergent Labs (hebergement) — Etats-Unis — Clauses contractuelles types
- MongoDB Atlas (base de donnees) — Union Europeenne

5. VOS DROITS (RGPD - Articles 15 a 22)
Vous disposez des droits suivants :
- Droit d'acces : obtenir une copie de vos donnees
- Droit de rectification : corriger des donnees inexactes
- Droit a l'effacement : demander la suppression de vos donnees
- Droit a la portabilite : recevoir vos donnees dans un format structure
- Droit d'opposition : vous opposer au traitement de vos donnees
- Droit de retrait du consentement : a tout moment, sans motif

Pour exercer ces droits, contactez-nous a : bonjour@monbeausite.com
Nous repondrons dans un delai de 30 jours maximum.

6. DESINSCRIPTION DES COMMUNICATIONS MARKETING
Chaque email marketing contient un lien "Se desinscrire" en bas de page. Vous pouvez egalement nous ecrire a bonjour@monbeausite.com.

7. SECURITE
Nous mettons en oeuvre des mesures techniques et organisationnelles pour proteger vos donnees :
- Chiffrement HTTPS (TLS)
- Mots de passe haches (bcrypt)
- Cookies securises (httpOnly, secure, sameSite)
- Acces restreint aux donnees (authentification admin)

8. RECLAMATION
Si vous estimez que vos droits ne sont pas respectes, vous pouvez introduire une reclamation aupres de la CNIL :
Commission Nationale de l'Informatique et des Libertes
3 Place de Fontenoy, 75007 Paris
www.cnil.fr

9. MODIFICATION DE CETTE POLITIQUE
Cette politique peut etre mise a jour. La date de derniere mise a jour figure en haut de cette page.

---
TVA non applicable, article 293 B du CGI.`}
    </LegalLayout>
  );
}
